import { FC, useMemo, useEffect, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Icon, { IconNames } from 'components/Icon';

const Font = Quill.import('formats/font') as any;
Font.whitelist = ['sans-serif', 'serif', 'monospace', 'dmsans'];
Quill.register(Font, true);

interface Props {
  id: string;
  value: string;
  onChange: (value: string) => void;
  valueState?: string;
  placeholder?: string;
}

// Custom toolbar component
const CustomToolbar: FC<{ id: string }> = ({ id }) => (
  <div id={`toolbar-${id}`} className="quill-custom-toolbar">
    <select className="ql-font" title="Font">
      <option value="">Default</option>
      <option value="dmsans">DMSans</option>
      <option value="serif">Serif</option>
      <option value="monospace">Monospace</option>
    </select>
    <select className="ql-size" title="Size">
      <option value="small">Small</option>
      <option value="">Normal</option>
      <option value="large">Large</option>
      <option value="huge">Huge</option>
    </select>
    <button className="ql-bold" title="Bold">
      <Icon name={IconNames.Bold} size={16} />
    </button>
    <button className="ql-italic" title="Italic">
      <Icon name={IconNames.Italic} size={16} />
    </button>
    <button className="ql-underline" title="Underline">
      <Icon name={IconNames.Underline} size={16} />
    </button>
    <button className="ql-link" title="Insert Link">
      <Icon name={IconNames.Embed} size={16} />
    </button>
    <button className="ql-list" value="ordered" title="Numbered List">
      <Icon name={IconNames.ListNumbered} size={16} />
    </button>
    <button className="ql-list" value="bullet" title="Bulleted List">
      <Icon name={IconNames.ListBulleted} size={16} />
    </button>
    <button className="ql-align" value="" title="Align Left">
      <Icon name={IconNames.ParagraphLeft} size={16} />
    </button>
    <button className="ql-align" value="center" title="Align Center">
      <Icon name={IconNames.ParagraphCenter} size={16} />
    </button>
    <button className="ql-align" value="right" title="Align Right">
      <Icon name={IconNames.ParagraphRight} size={16} />
    </button>
  </div>
);

export const QuillInput: FC<Props> = ({ value, onChange, id, valueState, placeholder }) => {
  const quillRef = useRef<ReactQuill>(null);

  // Sanitize the id so it's safe for use as a DOM id and CSS selector
  // Replace any characters that could break querySelector (e.g., dots) with dashes
  const safeId = useMemo(() => id.replace(/[^a-zA-Z0-9_-]/g, '-'), [id]);

  // Ensure tooltip never gets hidden by sidebar (min 10px from left)
  useEffect(() => {
    const adjustTooltipPosition = () => {
      const tooltip = document.querySelector('.ql-tooltip') as HTMLElement;
      if (tooltip && tooltip.style.left) {
        const leftValue = parseInt(tooltip.style.left);
        if (leftValue < 10) {
          tooltip.style.left = '10px';
        }
      }
    };

    // Monitor for tooltip changes
    const observer = new MutationObserver(adjustTooltipPosition);
    const container = quillRef.current?.getEditor().root.closest('.quill-wrapper');

    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style'],
      });
    }

    return () => observer.disconnect();
  }, []);

  // Configure toolbar modules to use custom toolbar with unique ID
  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#toolbar-${safeId}`,
      },
    }),
    [safeId]
  );

  // Configure formats that Quill should support
  const formats = [
    'font',
    'size',
    'bold',
    'italic',
    'underline',
    'link',
    'list',
    'bullet',
    'align',
  ];

  return (
    <div className={`quill-wrapper ${valueState}`}>
      <CustomToolbar id={safeId} />
      <ReactQuill
        ref={quillRef}
        id={safeId}
        theme="snow"
        value={value || ''}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder || 'Recommended 900 characters max'}
      />
    </div>
  );
};
